import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "lumir-back-office-ui";
import { TextDisplay } from "lumir-back-office-ui/RmsTextDisplay";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import recruitServiceV1 from "../../../api/renewal/recruit.service.v1";
import Dialog from "../../../components/templates/dialog";
import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
import useDisplay from "../../../hooks/useDisplay";
import { route } from "../../../router/route";
import {
  ApplyBasicInfo,
  ApplyResume,
  ApplyTerms,
  BackButton,
} from "./_component";
import { useApplyForm } from "./_component/apply/hooks/useApplyForm";

export default function Apply() {
  const { isMobile, isTablet } = useDisplay();
  const { lockScroll, openScroll } = useBodyScrollLock();

  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const title = searchParams.get("title");

  const [accessRoutes, setAccessRoutes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const { schema, defaultValues, formOptions } = useApplyForm();
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    const fetchAccessRoutes = async () => {
      const routes = await recruitServiceV1.getApplyAccessRoutes();
      setAccessRoutes(routes);
    };
    fetchAccessRoutes();
  }, []);

  const methods = useForm({
    ...formOptions,
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setFormError("");

      // 이력서는 필수, 포트폴리오는 선택사항
      const resumePromise = recruitServiceV1.postFileTempUploadUrl(
        data.resume.resume[0],
        "resume"
      );

      // 포트폴리오가 있는 경우에만 업로드 URL 요청
      const portfolioPromise =
        data.resume.portfolio && data.resume.portfolio.length > 0
          ? recruitServiceV1.postFileTempUploadUrl(
              data.resume.portfolio[0],
              "portfolio"
            )
          : Promise.resolve(null);

      const [resumeUrl, portfolioUrl] = await Promise.all([
        resumePromise,
        portfolioPromise,
      ]);

      await recruitServiceV1.applyRecruitWithTempFile(
        data,
        id,
        resumeUrl.uploadUrl,
        portfolioUrl?.uploadUrl || null
      );

      // 성공 시 다음 페이지로 이동
      navigate(route.applySuccess);
    } catch (error) {
      console.error("Submission error:", error.message);
      setFormError(error?.message);
      //searchParams 추가
      navigate(
        route.applyFailed +
          `?applyTitle=${title}&message=${
            error?.message || "제출 중 오류가 발생했습니다."
          }`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setIsOpen(false);
    openScroll();
    setFormError(null);
  };

  const handleDialogOpen = (data) => {
    // 포트폴리오 데이터 검증 및 정리
    if (
      !data.resume.portfolio ||
      !(data.resume.portfolio instanceof FileList) ||
      data.resume.portfolio.length === 0
    ) {
      data.resume.portfolio = new DataTransfer().files;
    }

    setFormData(data);
    setIsOpen(true);
    lockScroll();
  };

  return (
    <div id="tailwind-root">
      {isOpen && (
        <Dialog
          title="제출하시겠습니까?"
          description={
            isSubmitting
              ? `제출 중...`
              : formError
              ? formError
              : "제출 이후에는 서류를 수정할 수 없습니다."
          }
          leftButtonText="취소"
          rightButtonText="확인"
          leftButtonOnClick={handleDialogClose}
          rightButtonOnClick={() => formData && onSubmit(formData)}
          isLoading={isSubmitting}
        />
      )}
      <div
        className={`max-w-[720px] mx-auto mt-32 ${
          isMobile ? "px-[20px]" : isTablet ? "px-[24px]" : ""
        }`}
      >
        <BackButton isApply />
        <div className="mt-16 mb-[40px] space-y-[16px]">
          <p className="typography-semantic-title-2-bold">지원서 작성하기</p>
          <p className="typography-semantic-heading-3-regular">{title}</p>
        </div>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleDialogOpen)}>
            <ApplyBasicInfo name="basicInfo" accessRoutes={accessRoutes} />
            <ApplyResume name="resume" />
            <ApplyTerms name="terms" />

            {formError && (
              <TextDisplay
                className="mt-4 text-red-500 text-sm"
                text={{
                  description:
                    "제출 중 오류가 발생했습니다. 다시 시도해주세요.",
                }}
              />
            )}
            <div className="mt-[40px] mb-[60px]">
              <Button
                type="submit"
                size="xlg"
                variant="filled"
                state="primary"
                className="w-full h-[36px] cursor-none custom-cursor rounded-md"
                disabled={isSubmitting}
              >
                {isSubmitting ? "제출 중..." : "제출하기"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
